<script setup lang="ts">
import { UserSurveyIFrameDialog, UserSurveyStartDialog } from "#components";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import useAsync from "~/composables/use-async";
import useToast from "~/composables/use-toast";
import { SEGMENT_TRACK_EVENTS } from "~/constants";
import UserAPI from "~/services/user";
import useEventStore from "~/stores/event";
import useUserStore from "~/stores/user";

const { t } = useI18n();
const route = useRoute();
const toast = useToast();
const segment = useSegment();
const userStore = useUserStore();
const eventStore = useEventStore();
const runtimeConfig = useRuntimeConfig().public;

const isPxa = runtimeConfig.APP_FLAGS.isPxa;
const hasSurvey = runtimeConfig.APP_CUSTOMER.user_survey_form_url;
const surveyName = "survey_user_experience_1";

const excludedRoutes = [
  "auth-login",
  "auth-login-limit",
  "auth-oauth2-callback-provider",
  "auth-password-reset",
  "auth-password-forgot",
  "auth-sign-up",
  "auth-sign-up-personalization",
  "auth-sign-up-subscription",
  "auth-sign-up-subscription-code",
  "auth-sign-up-subscription-buy-id",
  "auth-sign-up-following-league",
  "auth-sign-up-following-league-leagueId",
  "auth-sign-up-following-league-followed",
  "auth-sign-up-following-category",
  "auth-sign-up-following-category-categoryId",
  "auth-sign-up-following-category-followed",
  "payment-failure",
  "payment-success",
  "payment-processing",
  "redirect-events-referenceId",
  "club",
  "open-app",
];

const surveyTimer = ref<NodeJS.Timeout | null>(null);
const surveyToastInstance = ref<{ destroy: () => void } | null>(null);
const isComponentActive = ref(true);

const isRouteExcluded = computed(() => excludedRoutes.includes(route.name as string));
const isUserEligible = computed(() => userStore.isAuthenticated && !!userStore.user);

const { run: skipSurvey } = useAsync(() => UserAPI.skipSurvey());
const { run: submitSurvey } = useAsync(() => UserAPI.submitSurvey());
const { run: closeSurvey } = useAsync(() => UserAPI.closeSurvey());

function destroySurveyToast() {
  if (surveyToastInstance.value) {
    toast.destroy(surveyToastInstance.value);
    surveyToastInstance.value = null;
  }
}

async function onClickLater() {
  await skipSurvey();
  if (
    eventStore?.player
    && eventStore?.playerState?.paused
    && !eventStore?.player.isFullscreen()
  ) {
    eventStore.player.play();
  }
}

async function onClickSubmit() {
  await submitSurvey();
  if (eventStore?.player && eventStore?.playerState?.paused) {
    eventStore.player.play();
  }
}

async function onClickClose() {
  await closeSurvey();
  if (
    eventStore?.player
    && eventStore?.playerState?.paused
    && !eventStore?.player.isFullscreen()
  ) {
    eventStore.player.play();
  }
}

function onClickGoAhead() {
  destroySurveyToast();
  surveyToastInstance.value = toast.custom(UserSurveyIFrameDialog, {
    position: "bottom-right",
    url: runtimeConfig.APP_CUSTOMER.user_survey_form_url,
    duration: false,
    onClose: async () => {
      await destroySurveyToast();
      await onClickClose();

      segment.track(SEGMENT_TRACK_EVENTS.USER_CLOSED_SURVEY_WINDOW);
    },
    onClick: async () => {
      await destroySurveyToast();
      await onClickSubmit();

      segment.track(SEGMENT_TRACK_EVENTS.USER_SUBMITTED_SURVEY);
    },
  });
}

function onClickStartSurvey() {
  destroySurveyToast();
  surveyToastInstance.value = toast.custom(UserSurveyStartDialog, {
    position: "bottom-right",
    title: computed(() => t("labels.user_survey.modal_title")),
    description: computed(() => t("labels.user_survey.modal_description")),
    cancelLabel: computed(() => t("labels.user_survey.later_label")),
    confirmLabel: computed(() => t("labels.user_survey.start_survey_label")),
    duration: false,
    onClose: async () => {
      await destroySurveyToast();
      await onClickLater();

      segment.track(SEGMENT_TRACK_EVENTS.USER_CLICKED_LATER_FOR_THE_SURVEY);
    },
    onClick: async () => {
      await destroySurveyToast();
      await onClickGoAhead();

      segment.track(SEGMENT_TRACK_EVENTS.USER_OPENED_SURVEY);
    },
  });

  if (
    eventStore?.player
    && eventStore?.playerState?.playing
    && !eventStore?.player.isFullscreen()
  ) {
    eventStore.player.pause();
  }
}

function clearSurveyTimer() {
  if (surveyTimer.value) {
    clearTimeout(surveyTimer.value);
    surveyTimer.value = null;
  }
}

const shouldShowSurvey = computed((): boolean => {
  if (!hasSurvey)
    return false;
  if (isRouteExcluded.value)
    return false;
  if (!isUserEligible.value)
    return false;

  const { added, options } = userStore.user!;
  const surveyData = options?.[surveyName];
  if (!added)
    return false;

  const surveyEligibilityThreshold = new Date();

  if (isPxa) {
    surveyEligibilityThreshold.setMinutes(surveyEligibilityThreshold.getMinutes() - 30);
  }
  else {
    surveyEligibilityThreshold.setDate(surveyEligibilityThreshold.getDate() - 7);
  }
  const userAddedDate = new Date(added);

  if (userAddedDate > surveyEligibilityThreshold)
    return false;

  if (surveyData?.submitted)
    return false;

  if (surveyData?.closed) {
    return false;
  }

  if (surveyData?.skipped) {
    const skippedDate = new Date(surveyData.skipped);
    const nextShowDate = new Date(skippedDate);
    const delayMinutes = isPxa ? 10 : 1440;
    nextShowDate.setMinutes(nextShowDate.getMinutes() + delayMinutes);
    return new Date() >= nextShowDate;
  }
  return true;
});

function triggerSurveyWithDelay() {
  if (!isComponentActive.value || !shouldShowSurvey.value) {
    return;
  }

  const delayMs = isPxa ? 5000 : 20000;
  surveyTimer.value = setTimeout(() => {
    if (isComponentActive.value && shouldShowSurvey.value) {
      onClickStartSurvey();
    }
  }, delayMs);
}

watch(
  isRouteExcluded,
  (isExcluded) => {
    if (isExcluded) {
      clearSurveyTimer();
      destroySurveyToast();
    }
    else {
      nextTick(() => {
        clearSurveyTimer();
        triggerSurveyWithDelay();
      });
    }
  },
  { immediate: false },
);

watch(
  isUserEligible,
  (isEligible) => {
    if (!isEligible) {
      clearSurveyTimer();
      destroySurveyToast();
    }
  },
  { immediate: false },
);

watch(
  shouldShowSurvey,
  (canShow, wasShowing) => {
    if (wasShowing !== undefined) {
      if (!canShow) {
        clearSurveyTimer();
        destroySurveyToast();
      }
      else if (canShow && !isRouteExcluded.value) {
        clearSurveyTimer();
        triggerSurveyWithDelay();
      }
    }
  },
);

onMounted(() => {
  isComponentActive.value = true;
  nextTick(() => {
    triggerSurveyWithDelay();
  });
});

onBeforeUnmount(() => {
  isComponentActive.value = false;
  clearSurveyTimer();
  destroySurveyToast();
});
</script>

<template>
  <div />
</template>
